import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const BackupManagement = () => {
  const [backups, setBackups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => { fetchBackups(); fetchLogs(); }, []);

  const token = () => localStorage.getItem('admin_token');

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/api/admin/backups`, { headers: { Authorization: `Bearer ${token()}` } });
      setBackups(resp.data.backups || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchLogs = async () => {
    try {
      const resp = await axios.get(`${API}/api/admin/backups/logs`, { headers: { Authorization: `Bearer ${token()}` } });
      setLogs(resp.data.logs || []);
    } catch (e) { console.error(e); }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/api/admin/backups/create`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      alert('Backup created successfully!');
      fetchBackups();
      fetchLogs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Backup failed');
    } finally { setCreating(false); }
  };

  const restoreBackup = async (filename) => {
    setRestoring(filename);
    try {
      await axios.post(`${API}/api/admin/backups/restore/${filename}`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      alert('Database restored successfully! Please refresh.');
      setConfirmRestore(null);
      fetchLogs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Restore failed');
    } finally { setRestoring(null); }
  };

  const deleteBackup = async (filename) => {
    try {
      await axios.delete(`${API}/api/admin/backups/${filename}`, { headers: { Authorization: `Bearer ${token()}` } });
      setConfirmDelete(null);
      fetchBackups();
    } catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const cleanupOld = async () => {
    try {
      const resp = await axios.post(`${API}/api/admin/backups/cleanup`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      alert(resp.data.message);
      fetchBackups();
    } catch (err) { alert('Cleanup failed'); }
  };

  const formatSize = (mb) => mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb} MB`;
  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div data-testid="backup-management-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Backup Management</h1>
          <p className="text-slate-500">Database backups stored on Bunny CDN | 5-day auto retention | Daily 2 AM UTC</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { setShowLogs(!showLogs); if (!showLogs) fetchLogs(); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium ${showLogs ? 'bg-slate-800 text-white' : 'border border-slate-200 hover:bg-slate-50'}`}
            data-testid="toggle-logs-btn">
            {showLogs ? 'View Backups' : 'Activity Logs'}
          </button>
          <button onClick={cleanupOld} className="px-4 py-2 border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50" data-testid="cleanup-btn">
            Cleanup Old
          </button>
          <button onClick={createBackup} disabled={creating}
            className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-2 rounded-xl font-medium hover:shadow-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="create-backup-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            {creating ? 'Creating...' : 'Create Backup Now'}
          </button>
        </div>
      </div>

      {showLogs ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h2 className="font-semibold text-slate-800">Activity Logs</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">File</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Size</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No logs yet</td></tr>
              ) : logs.map((log, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs">{formatDate(log.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      log.type === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                      log.type === 'manual' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'
                    }`}>{log.type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${log.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{log.status}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{log.filename || '-'}</td>
                  <td className="px-4 py-3 text-xs">{log.size_mb ? `${log.size_mb} MB` : '-'}</td>
                  <td className="px-4 py-3 text-xs">{log.created_by || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div></div>
          ) : backups.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No Backups Found</h3>
              <p className="text-slate-400 mb-4">Create your first backup to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map(backup => (
                <div key={backup.filename} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:shadow-sm transition-all" data-testid={`backup-${backup.filename}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{backup.filename}</p>
                      <p className="text-xs text-slate-500">{backup.date} | {formatSize(backup.size_mb)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={backup.cdn_url} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid={`download-${backup.filename}`}>
                      Download
                    </a>
                    <button onClick={() => setConfirmRestore(backup)}
                      className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-200" data-testid={`restore-${backup.filename}`}>
                      Restore
                    </button>
                    <button onClick={() => setConfirmDelete(backup)}
                      className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs font-medium hover:bg-red-200" data-testid={`delete-${backup.filename}`}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Confirm Restore Modal */}
      {confirmRestore && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              </div>
              <h3 className="text-lg font-bold text-slate-800">Restore Database?</h3>
              <p className="text-sm text-slate-500 mt-2">This will <strong className="text-red-600">overwrite all current data</strong> with the backup from:</p>
              <p className="text-sm font-semibold text-slate-700 mt-1">{confirmRestore.date}</p>
              <p className="text-xs text-red-500 mt-2">This action cannot be undone. Create a backup first if needed.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmRestore(null)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium">Cancel</button>
              <button onClick={() => restoreBackup(confirmRestore.filename)} disabled={!!restoring}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50" data-testid="confirm-restore-btn">
                {restoring ? 'Restoring...' : 'Yes, Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Delete Backup?</h3>
            <p className="text-sm text-slate-500 mb-4">Delete <strong>{confirmDelete.filename}</strong> from CDN? This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2 border border-slate-200 rounded-xl text-sm">Cancel</button>
              <button onClick={() => deleteBackup(confirmDelete.filename)} className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupManagement;
